import { PanelError } from '@/components/admin-panel-state.component';
import { AppLayout } from '@/components/app-layout';
import { AuthCheck } from '@/components/auth-check.component';
import { Head } from '@/components/head.component';
import { FinanceArea } from '@/containers/finance/finance-area.component';
import {
  DEFAULT_FINANCE_VIEW,
  type FinanceView,
  isFinanceView,
} from '@/containers/finance/finance-view';
import { useGetFinanceAccessMeQuery } from '@/store/features/admin-finance.feature';
import { useUser } from '@clerk/react';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect } from 'react';

const STAFF_DOMAIN = 'cleanlist.ai';

/**
 * /finance: staff on the finance list only, kept out of Management so a
 * shared Management screen never shows it. Nothing renders until the list
 * answers yes; staff off the list go to Management, everyone else home.
 */
const FinancePage = () => {
  const { user, isSignedIn } = useUser();
  const navigate = useNavigate();
  const { view } = useSearch({ from: '/finance' });
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const isStaff = email.toLowerCase().endsWith(`@${STAFF_DOMAIN}`);
  const me = useGetFinanceAccessMeQuery(undefined, { skip: !isStaff });
  const refused = me.data?.allowed === false;

  useEffect(() => {
    if (isSignedIn && !isStaff) navigate({ to: '/', replace: true });
    else if (isSignedIn && refused)
      navigate({
        to: '/management',
        search: { tab: 'overview' },
        replace: true,
      });
  }, [isSignedIn, isStaff, refused, navigate]);

  if (!isSignedIn || !isStaff) return null;
  if (!me.data?.allowed)
    return me.isError && !me.isFetching ? (
      <AppLayout>
        <PanelError
          what='finance access'
          error={me.error}
          onRetry={() => void me.refetch()}
        />
      </AppLayout>
    ) : null;

  return (
    <AppLayout>
      <FinanceArea view={view} meEmail={me.data.email} />
    </AppLayout>
  );
};

export const Route = createFileRoute('/finance')({
  validateSearch: (search: Record<string, unknown>): { view: FinanceView } => ({
    view: isFinanceView(search.view) ? search.view : DEFAULT_FINANCE_VIEW,
  }),
  component: () => (
    <AuthCheck>
      <Head heading='Finance' />
      <FinancePage />
    </AuthCheck>
  ),
});
